import {
  createBrowserRouter,
  Navigate,
  Outlet,
  ScrollRestoration,
} from "react-router";
import {
  RedirectIfAuthed,
  RequireApprovedPro,
  RequireOnboardedPro,
  RequirePro,
  RequireVerifiedPro,
} from "../lib/use-auth";
import { RequireCustomer } from "../lib/use-customer-auth";
import { RequireBookingAddress } from "../lib/booking-guard";
import ErrorPage from "./pages/ErrorPage";
import LandingPage from "./pages/LandingPage";
import HomePage from "./pages/HomePage";
import SignUpPage from "./pages/SignUpPage";
import SignUpStep2Page from "./pages/SignUpStep2Page";
import SignUpSuccessPage from "./pages/SignUpSuccessPage";
import SignInPage from "./pages/SignInPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import CustomerVerifyEmailPage from "./pages/VerifyEmailPage";
import InboxPage from "./pages/InboxPage";
import ProInboxPage from "./pages/professional/InboxPage";
import WalletPage from "./pages/WalletPage";
import BookingLocationPage from "./pages/BookingLocationPage";
import BookingFlowPage from "./pages/BookingFlowPage";
import BookingServicePage from "./pages/BookingServicePage";
import BookingDetailsPage from "./pages/BookingDetailsPage";
import NewToCherryPage from "./pages/NewToCherryPage";
import ContinueBookingPage from "./pages/ContinueBookingPage";
import BookingConfirmationPage from "./pages/BookingConfirmationPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import YourProfilePage from "./pages/YourProfilePage";
import AccountInfoPage from "./pages/AccountInfoPage";
import VoucherPage from "./pages/VoucherPage";
import SecurityPage from "./pages/SecurityPage";
import ChangePasswordLoginPage from "./pages/ChangePasswordLoginPage";
import BillingSubscriptionPage from "./pages/BillingSubscriptionPage";
import NotificationsPage from "./pages/NotificationsPage";
import PrivacyPage from "./pages/PrivacyPage";
import MessengerPage from "./pages/MessengerPage";
import NotFoundPage from "./pages/NotFoundPage";
import LandingPageLoggedIn from "./pages/LandingPageLoggedIn";
import HelpCenterPage from "./pages/HelpCenterPage";
import HelpArticlePage from "./pages/HelpArticlePage";
import SecondaryPreferredPage from "./pages/SecondaryPreferredPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import PublicReviewsPage from "./pages/PublicReviewsPage";
import SubscriptionDetailPage from "./pages/SubscriptionDetailPage";
import ServicePlacePage from "./pages/ServicePlacePage";
import SelectProfessionalPage from "./pages/SelectProfessionalPage";
import YourAppointmentPage from "./pages/YourAppointmentPage";
import SelectTreatmentPage from "./pages/SelectTreatmentPage";
import GiftCardPage from "./pages/GiftCardPage";
import GiftCardRecipientPage from "./pages/GiftCardRecipientPage";
import GiftCardDeliveryPage from "./pages/GiftCardDeliveryPage";
import GiftCardSummaryPage from "./pages/GiftCardSummaryPage";
import GiftCardSentPage from "./pages/GiftCardSentPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import AboutUsPage from "./pages/AboutUsPage";
import CancellationPolicyPage from "./pages/CancellationPolicyPage";
import ContactUsPage from "./pages/ContactUsPage";

import ProSignUpPage from "./pages/professional/SignUpPage";
import ProJoinPage from "./pages/professional/JoinPage";
import ProSignInPage from "./pages/professional/SignInPage";
import ProForgotPasswordPage from "./pages/professional/ForgotPasswordPage";
import ProfessionalLandingPage from "./pages/professional/LandingPage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import ProSignUpStep2Page from "./pages/professional/SignUpStep2Page";
import ProSignUpSuccessPage from "./pages/professional/SignUpSuccessPage";
import VerifyYourIdentityPage from "./pages/professional/VerifyYourIdentityPage";
import VerifyEmailPage from "./pages/professional/VerifyEmailPage";
import SubmitPassportPage from "./pages/professional/SubmitPassportPage";
import StartRecordingPage from "./pages/professional/StartRecordingPage";
import VerificationCompletePage from "./pages/professional/VerificationCompletePage";
import ServiceOfferPage from "./pages/professional/ServiceOfferPage";
import ProDashboardPage from "./pages/professional/Dashboard";
import ProEarningsPage from "./pages/professional/EarningsPage";
import ProPayoutsPage from "./pages/professional/PayoutsPage";
import SetupPayoutsPage from "./pages/professional/SetupPayoutsPage";
import MessengerPro from "./pages/professional/MessengerPro";
import ProServicesPage from "./pages/professional/ProServicesPage";
import ProClientRatesPage from "./pages/professional/ProClientRatesPage";
import ProSchedulePage from "./pages/professional/ProSchedulePage";
import ProHelpCenterPage from "./pages/professional/ProHelpCenterPage";
import AttendanceManagePage from "./pages/professional/AttendanceManagePage";
import AttendanceConfirmAllPage from "./pages/professional/AttendanceConfirmAllPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import BookingsPage from "./pages/professional/BookingsPage";
import ProBookingDetailPage from "./pages/professional/BookingDetailPage";
import ReviewsPage from "./pages/professional/ReviewsPage";
import ProAccountSettingsPage from "./pages/professional/AccountSettingsPage";
import ProAccountInfoPage from "./pages/professional/AccountInfoPage";
import ProSecurityPage from "./pages/professional/SecurityPage";
import ProChangePasswordLoginPage from "./pages/professional/ChangePasswordLoginPage";
import ProBillingSubscriptionPage from "./pages/professional/BillingSubscriptionPage";
import ProNotificationsPage from "./pages/professional/NotificationsPage";
import ProPrivacyPage from "./pages/professional/PrivacyPage";
import ProPublicProfilePage from "./pages/professional/PublicProfilePage";
import ProPortfolioPage from "./pages/professional/ProPortfolioPage";
import ProAboutEditPage from "./pages/professional/ProAboutEditPage";
import ProServicesEditPage from "./pages/professional/ProServicesEditPage";

// Root layout for every route. <ScrollRestoration> resets the window to
// the top on each new navigation (and restores the prior offset on
// back/forward), so a page never opens at the previous page's scroll
// position — fixes landing on the booking flow stuck at the footer after
// scrolling down the homepage.
function RootLayout() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
  {
    // The public marketing page is for logged-out visitors. Anyone
    // already authenticated (pro mid-onboarding, customer mid-signup,
    // or an approved customer/pro) gets routed to their own home via
    // RedirectIfAuthed so the logo-click from any inner page doesn't
    // dump them onto the Sign In / Become a Pro hero they no longer
    // need.
    path: "/",
    element: (
      <RedirectIfAuthed>
        <LandingPage />
      </RedirectIfAuthed>
    ),
  },
  {
    path: "/user-landing",
    element: (
      <RequireCustomer>
        <LandingPageLoggedIn />
      </RequireCustomer>
    ),
  },
  {
    path: "/professional-landing",
    Component: ProfessionalLandingPage,
  },
  {
    path: "/professional/account-settings",
    element: (
      <RequirePro>
        <ProAccountSettingsPage />
      </RequirePro>
    ),
  },
  {
    path: "/professional/account-info",
    element: (
      <RequirePro>
        <ProAccountInfoPage />
      </RequirePro>
    ),
  },
  {
    path: "/professional/security",
    element: (
      <RequirePro>
        <ProSecurityPage />
      </RequirePro>
    ),
  },
  {
    path: "/professional/change-password-login",
    element: (
      <RequirePro>
        <ProChangePasswordLoginPage />
      </RequirePro>
    ),
  },
  {
    path: "/professional/billing-subscription",
    element: (
      <RequirePro>
        <ProBillingSubscriptionPage />
      </RequirePro>
    ),
  },
  {
    path: "/professional/notifications",
    element: (
      <RequirePro>
        <ProNotificationsPage />
      </RequirePro>
    ),
  },
  {
    path: "/professional/privacy",
    element: (
      <RequirePro>
        <ProPrivacyPage />
      </RequirePro>
    ),
  },
  {
    path: "/professional/public-profile",
    element: (
      <RequireOnboardedPro>
        <ProPublicProfilePage />
      </RequireOnboardedPro>
    ),
  },
  {
    path: "/professional/portfolio",
    element: (
      <RequireApprovedPro>
        <ProPortfolioPage />
      </RequireApprovedPro>
    ),
  },
  {
    path: "/professional/about-edit",
    element: (
      <RequireApprovedPro>
        <ProAboutEditPage />
      </RequireApprovedPro>
    ),
  },
  {
    path: "/professional/services-edit",
    element: (
      <RequireApprovedPro>
        <ProServicesEditPage />
      </RequireApprovedPro>
    ),
  },
  {
    path: "/home",
    Component: HomePage,
  },
  {
    // The role-chooser screen is gone — sign-up now defaults to the
    // customer tab and the visitor flips to Professional via the tabs
    // on the page. Old links/bookmarks to /signup land on that default.
    path: "/signup",
    element: <Navigate to="/signup/customer" replace />,
  },
  {
    path: "/signup/customer",
    element: (
      <RedirectIfAuthed>
        <SignUpPage />
      </RedirectIfAuthed>
    ),
  },
  {
    // Public self-service account deletion (Play Store / App Store requirement).
    // No auth — the user proves ownership with email + password on the page.
    path: "/delete-account",
    Component: DeleteAccountPage,
  },
  {
    path: "/professional/signup",
    element: (
      <RedirectIfAuthed>
        <ProSignUpPage />
      </RedirectIfAuthed>
    ),
  },
  {
    // Invite-only account creation — the tokenized "skip the application"
    // link sent to approved applicants / off-platform recruits.
    path: "/professional/join/:token",
    Component: ProJoinPage,
  },
  {
    path: "/professional/signup/step2",
    Component: ProSignUpStep2Page,
  },
  {
    path: "/professional/signup/success",
    Component: ProSignUpSuccessPage,
  },
  {
    path: "/professional/verify-identity",
    Component: VerifyYourIdentityPage,
  },
  {
    path: "/professional/verify-email",
    Component: VerifyEmailPage,
  },
  {
    path: "/professional/submit-passport",
    Component: SubmitPassportPage,
  },
  {
    path: "/professional/start-recording",
    Component: StartRecordingPage,
  },
  {
    path: "/professional/verification-complete",
    Component: VerificationCompletePage,
  },
  {
    // Onboarding wizard — kicks in after the pro finishes uploading
    // identity documents but before they're APPROVED. Guarded by
    // RequireVerifiedPro (email confirmed) rather than RequireApprovedPro
    // so pending pros can reach it.
    path: "/professional/service-offer",
    element: (
      <RequireVerifiedPro>
        <ServiceOfferPage />
      </RequireVerifiedPro>
    ),
  },
  {
    path: "/professional/dashboard",
    element: (
      <RequireOnboardedPro>
        <ProDashboardPage />
      </RequireOnboardedPro>
    ),
  },
  {
    path: "/professional/earnings",
    element: (
      <RequireOnboardedPro>
        <ProEarningsPage />
      </RequireOnboardedPro>
    ),
  },
  {
    path: "/professional/payouts",
    element: (
      <RequireOnboardedPro>
        <ProPayoutsPage />
      </RequireOnboardedPro>
    ),
  },
  {
    // Stripe Connect hosted-onboarding kickoff + return handler.
    // RequirePro (not RequireOnboardedPro) — the pro is required to
    // pass through here BEFORE they finish onboarding, so we can't
    // gate this route on already-being-onboarded.
    path: "/professional/setup-payouts",
    element: (
      <RequirePro>
        <SetupPayoutsPage />
      </RequirePro>
    ),
  },
  {
    path: "/professional/messenger",
    element: (
      <RequireOnboardedPro>
        <MessengerPro />
      </RequireOnboardedPro>
    ),
  },
  {
    path: "/professional/services",
    element: (
      <RequireOnboardedPro>
        <ProServicesPage />
      </RequireOnboardedPro>
    ),
  },
  {
    path: "/professional/client-rates",
    element: (
      <RequireOnboardedPro>
        <ProClientRatesPage />
      </RequireOnboardedPro>
    ),
  },
  {
    path: "/professional/schedule",
    element: (
      <RequireOnboardedPro>
        <ProSchedulePage />
      </RequireOnboardedPro>
    ),
  },
  {
    path: "/professional/help-center",
    Component: ProHelpCenterPage,
  },
  // Token-authed per-booking management linked from the 7pm/9pm
  // attendance emails. No session JWT — auth is the signed `t` query
  // param. Backend: src/Controllers/Booking/ProBookingAttendance.
  {
    path: "/pro/attendance",
    Component: AttendanceManagePage,
  },
  {
    path: "/pro/attendance/confirm-all",
    Component: AttendanceConfirmAllPage,
  },
  // One-click marketing unsubscribe landing — UK GDPR Art. 7(3).
  // Token in ?t= is signed by the backend and carries the user id.
  {
    path: "/unsubscribe",
    Component: UnsubscribePage,
  },
  {
    path: "/professional/help-center/article/:code",
    element: <HelpArticlePage audience="PROFESSIONAL" />,
  },
  {
    path: "/professional/bookings",
    element: (
      <RequireOnboardedPro>
        <BookingsPage />
      </RequireOnboardedPro>
    ),
  },
  {
    path: "/professional/bookings/:id",
    element: (
      <RequireOnboardedPro>
        <ProBookingDetailPage />
      </RequireOnboardedPro>
    ),
  },
  {
    path: "/professional/reviews",
    element: (
      <RequireOnboardedPro>
        <ReviewsPage />
      </RequireOnboardedPro>
    ),
  },
  {
    path: "/professional/forgot-password",
    element: (
      <RedirectIfAuthed>
        <ProForgotPasswordPage />
      </RedirectIfAuthed>
    ),
  },
  {
    path: "/professional/signin",
    element: (
      <RedirectIfAuthed>
        <ProSignInPage />
      </RedirectIfAuthed>
    ),
  },
  {
    path: "/signup/step2",
    Component: SignUpStep2Page,
  },
  {
    path: "/signup/success",
    Component: SignUpSuccessPage,
  },
  {
    // Role-chooser removed — sign-in defaults to the customer tab and
    // the Professional tab on the page handles the switch. Old links to
    // /signin redirect here.
    path: "/signin",
    element: <Navigate to="/signin/customer" replace />,
  },
  {
    path: "/signin/customer",
    element: (
      <RedirectIfAuthed>
        <SignInPage />
      </RedirectIfAuthed>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <RedirectIfAuthed>
        <ForgotPasswordPage />
      </RedirectIfAuthed>
    ),
  },
  {
    path: "/verify-email",
    Component: CustomerVerifyEmailPage,
  },
  {
    path: "/inbox",
    element: (
      <RequireCustomer>
        <InboxPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/wallet",
    Component: WalletPage,
  },
  {
    path: "/professional/inbox",
    element: (
      <RequirePro>
        <ProInboxPage />
      </RequirePro>
    ),
  },
  {
    path: "/booking-location",
    Component: BookingLocationPage,
  },
  {
    path: "/booking",
    element: (
      <RequireBookingAddress>
        <BookingFlowPage />
      </RequireBookingAddress>
    ),
  },
  {
    path: "/service-place",
    Component: ServicePlacePage,
  },
  {
    path: "/select-treatment",
    element: (
      <RequireBookingAddress>
        <SelectTreatmentPage />
      </RequireBookingAddress>
    ),
  },
  {
    path: "/gift-card",
    Component: GiftCardPage,
  },
  {
    path: "/gift-card/recipient",
    Component: GiftCardRecipientPage,
  },
  {
    path: "/gift-card/delivery",
    Component: GiftCardDeliveryPage,
  },
  {
    path: "/gift-card/details",
    Component: GiftCardSummaryPage,
  },
  {
    path: "/gift-card/sent",
    Component: GiftCardSentPage,
  },
  {
    path: "/select-professional",
    element: (
      <RequireBookingAddress>
        <SelectProfessionalPage />
      </RequireBookingAddress>
    ),
  },
  {
    path: "/bookings",
    element: (
      <RequireCustomer>
        <BookingServicePage />
      </RequireCustomer>
    ),
  },
  {
    path: "/subscriptions/:id",
    element: (
      <RequireCustomer>
        <SubscriptionDetailPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/bookings/:id",
    element: (
      <RequireCustomer>
        <BookingDetailsPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/new-to-cherry",
    Component: NewToCherryPage,
  },
  {
    path: "/continue-booking",
    element: (
      <RequireCustomer>
        <RequireBookingAddress>
          <ContinueBookingPage />
        </RequireBookingAddress>
      </RequireCustomer>
    ),
  },
  {
    path: "/booking/confirmation",
    element: (
      <RequireCustomer>
        <BookingConfirmationPage />
      </RequireCustomer>
    ),
  },
  {
    // Stripe routes recurring checkouts back here; the same component
    // detects subscription mode from the path and calls the right
    // confirm endpoint.
    path: "/booking/subscription-confirmation",
    element: (
      <RequireCustomer>
        <BookingConfirmationPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/account-settings",
    element: (
      <RequireCustomer>
        <AccountSettingsPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/your-profile",
    element: (
      <RequireCustomer>
        <YourProfilePage />
      </RequireCustomer>
    ),
  },
  {
    path: "/account-info",
    element: (
      <RequireCustomer>
        <AccountInfoPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/vouchers",
    element: (
      <RequireCustomer>
        <VoucherPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/security",
    element: (
      <RequireCustomer>
        <SecurityPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/change-password-login",
    element: (
      <RequireCustomer>
        <ChangePasswordLoginPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/billing-subscription",
    element: (
      <RequireCustomer>
        <BillingSubscriptionPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/notifications",
    element: (
      <RequireCustomer>
        <NotificationsPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/privacy",
    element: (
      <RequireCustomer>
        <PrivacyPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/messenger",
    element: (
      <RequireCustomer>
        <MessengerPage />
      </RequireCustomer>
    ),
  },
  {
    path: "/help-center",
    Component: HelpCenterPage,
  },
  {
    path: "/help-center/article/:code",
    element: <HelpArticlePage audience="CUSTOMER" />,
  },
  {
    path: "/secondary-preferred",
    element: (
      <RequireBookingAddress>
        <SecondaryPreferredPage />
      </RequireBookingAddress>
    ),
  },
  {
    path: "/public-profile",
    element: (
      <RequireBookingAddress>
        <PublicProfilePage />
      </RequireBookingAddress>
    ),
  },
  {
    path: "/public-reviews",
    Component: PublicReviewsPage,
  },
  {
    path: "/your-appointment",
    element: (
      <RequireCustomer>
        <RequireBookingAddress>
          <YourAppointmentPage />
        </RequireBookingAddress>
      </RequireCustomer>
    ),
  },
  {
    path: "/terms-of-use",
    Component: TermsOfUsePage,
  },
  {
    path: "/privacy-policy",
    Component: PrivacyPolicyPage,
  },
  {
    path: "/about-us",
    Component: AboutUsPage,
  },
  {
    path: "/cancellation-policy",
    Component: CancellationPolicyPage,
  },
  {
    path: "/contact-us",
    Component: ContactUsPage,
  },
  {
    path: "*",
    Component: NotFoundPage,
  },
    ],
  },
]);
